import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import {
  CrossCloseIcon,
  MenuIcon,
  SearchIcon,
  SupportDudeIcon,
  UserIcon,
} from '@common/utils/icons/icons';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectCurrentUserRole, selectUser } from '../../store/general/general-slice-selectors';
import UserRole from '../../models/enums/user-roles';
import UserTopBarPopover from '../user/UserTopBarPopover';
import RoleTopBarPopover from '../user/RoleTopBarPopover';
import SupportDudePopover from '../user/SupportDudePopover';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import MainNavigationBar from './MainNavigationBar';
import SearchBar from './SearchBar';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import OrganizationRole from '../../models/enums/organization-roles';
import { useNavigate } from 'react-router-dom';
import { navigateSphereWithFilters } from '@common/enums/query-params/filters';
import { setSelectedAgreementId } from 'store/general/general-slice';
import SphereLogo from '../../media/logo512.png';
import TopBarHeaderText from './TopBarHeaderText';
import AdditionalButtons from './AdditionalButtons';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent, HelpLink, HelpLinkSource } from 'services/analytics-events';

const TopBar: React.FC<{ isNavOpen: boolean; onNavChangeHandler: () => void }> = ({
  isNavOpen,
  onNavChangeHandler,
}) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const matchesLgDown = useMediaQuery(theme.breakpoints.down('lg'));

  const user = useAppSelector((state) => selectUser(state));
  const userRole = useAppSelector((state) => selectCurrentUserRole(state));

  const [userAnchorEl, setUserAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [roleAnchorEl, setRoleAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [supportDudeAnchorEl, setSupportDudeAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [isSmallScreenSearchOpen, setIsSmallScreenSearchOpen] = useState(false);

  const openUserPopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserAnchorEl(event.currentTarget);
  };

  const openRolePopoverClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    setRoleAnchorEl(event.currentTarget);
  };

  const openSupportDudePopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackEvent(AnalyticsEvent.SupportPopoverOpened, {
      has_email: user.data.support.email != null,
      has_phone: user.data.support.phoneNumber != null,
      source_page: getCurrentPageName(),
    });
    setSupportDudeAnchorEl(event.currentTarget);
  };

  const closeUserPopoverHandler = () => {
    setUserAnchorEl(null);
  };

  const closeSupportDudePopoverHandler = () => {
    setSupportDudeAnchorEl(null);
  };

  const closeRolePopoverHandler = () => {
    setRoleAnchorEl(null);
  };

  const openSmallScreenSearchClickHandler = () => {
    setIsSmallScreenSearchOpen(true);
  };

  const closeSmallScreenHandler = () => {
    setIsSmallScreenSearchOpen(false);
  };

  const navigateClickHandler = (id: string) => {
    navigate(navigateSphereWithFilters(id, true));
    dispatch(setSelectedAgreementId(null));
    if (matchesSmDown) {
      onNavChangeHandler();
    }
  };

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          backgroundColor: matchesSmDown
            ? theme.palette.background.paper
            : theme.palette.background.default,
          boxShadow: matchesSmDown ? '0 3px 5px -1px rgba(0,0,0,0.2)' : undefined,
          borderRadius: { xs: 0, sm: theme.spacing(1) },
          width: '100%',
          maxWidth: '100vw',
          py: 1,
          zIndex: 1,
        })}
      >
        {isSmallScreenSearchOpen ? (
          matchesSmDown ? (
            <Box pt={0.5}>
              <SearchBar isSmallScreenSearch onClose={closeSmallScreenHandler} includeHistory />
            </Box>
          ) : (
            <SearchBar isSmallScreenSearch onClose={closeSmallScreenHandler} includeHistory />
          )
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            maxWidth="100vw"
            justifyContent="space-between"
            spacing={1}
            sx={(theme) => ({ height: theme.spacing(6) })}
          >
            <Stack direction="row" alignItems="center" sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, flexShrink: 0 }}>
                {matchesSmDown ? (
                  <IconButton
                    onClick={onNavChangeHandler}
                    sx={{ p: 0.5 }}
                    aria-label={
                      isNavOpen ? 'label-close-navigation-menu' : 'label-open-navigation-menu'
                    }
                    aria-expanded={isNavOpen}
                    aria-controls="topbar-mobile-navigation-menu"
                  >
                    {isNavOpen ? (
                      <CrossCloseIcon sx={{ fontSize: 28 }} />
                    ) : (
                      <MenuIcon sx={{ fontSize: 28 }} />
                    )}
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => navigateClickHandler(user.data.organization.id)}
                    sx={{ p: 0.5 }}
                  >
                    <img
                      src={SphereLogo}
                      alt=""
                      style={{ width: theme.spacing(5), height: theme.spacing(5) }}
                    />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
                <TopBarHeaderText />
              </Box>
            </Stack>
            {!matchesMdDown && <SearchBar includeHistory />}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              pr={matchesSmDown ? 1 : 2}
              sx={{ flexShrink: 0 }}
            >
              {matchesMdDown && (
                <TooltipIconButton
                  tooltip={t('input-label-search')}
                  icon={<SearchIcon />}
                  onClick={openSmallScreenSearchClickHandler}
                />
              )}
              {user.data.organization.role === OrganizationRole.MANUFACTURER && (
                <Tooltip title={t('user-role-description-' + userRole)} placement="left">
                  <Chip
                    onClick={
                      user.data.role !== UserRole.MEMBER ? openRolePopoverClickHandler : undefined
                    }
                    variant="outlined"
                    sx={{
                      display: { xs: 'none', sm: 'inherit' },
                      cursor: user.data.role !== UserRole.MEMBER ? 'pointer' : undefined,
                    }}
                    label={t('user-role-' + userRole)}
                    color={
                      userRole === UserRole.OWNER
                        ? 'success'
                        : userRole === UserRole.ADMIN
                          ? 'info'
                          : undefined
                    }
                  />
                </Tooltip>
              )}

              {!matchesLgDown && <AdditionalButtons />}

              <IconButton onClick={openUserPopoverClickHandler} aria-describedby="user-popover-id">
                <UserIcon data-testid="button-user-popup" />
              </IconButton>
              <IconButton
                onClick={openSupportDudePopoverClickHandler}
                aria-describedby="support-dude-popover-id"
              >
                <SupportDudeIcon data-testid="button-support-dude-popup" />
              </IconButton>
            </Stack>
          </Stack>
        )}
        {matchesSmDown && (
          <Collapse
            id="topbar-mobile-navigation-menu"
            in={isNavOpen}
            sx={{ pt: isNavOpen ? 1 : 0 }}
          >
            <MainNavigationBar isNavOpen={isNavOpen} onNavChangeHandler={onNavChangeHandler} />
          </Collapse>
        )}
      </Stack>
      <UserTopBarPopover
        user={user.data}
        anchorEl={userAnchorEl}
        onClose={closeUserPopoverHandler}
      />
      <RoleTopBarPopover
        user={user.data}
        anchorEl={roleAnchorEl}
        onClose={closeRolePopoverHandler}
      />
      <SupportDudePopover anchorEl={supportDudeAnchorEl} onClose={closeSupportDudePopoverHandler} />
    </React.Fragment>
  );
};

export default TopBar;
